import { useEffect, useState, useRef } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, Loader2, AlertCircle, Landmark } from "lucide-react";
import axios from "axios";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog per l'upload del PDF dell'estratto conto.
 * Dopo l'upload, chiama onSuccess con i dati estratti per passare alla revisione.
 */
export const ImportStatementDialog = ({ 
  open, 
  onOpenChange, 
  onSuccess, 
  getAuthHeaders,
  localeId,
  banchePos = [],
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (banchePos.length === 0) setSelectedBankId("__generic__");
    else if (banchePos.length === 1) setSelectedBankId(banchePos[0].id);
    else setSelectedBankId("");
  }, [open, banchePos]);

  const ACCEPTED_EXT = ['.pdf', '.xlsx', '.xls'];
  const isAccepted = (name) => {
    const lower = (name || '').toLowerCase();
    return ACCEPTED_EXT.some(ext => lower.endsWith(ext));
  };

  const handleFileSelect = (e) => {
    if (!selectedBankId) {
      toast.error("Seleziona prima la banca");
      return;
    }
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!isAccepted(selectedFile.name)) {
        toast.error("Seleziona un file PDF o Excel (.xlsx/.xls)");
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!selectedBankId) {
      toast.error("Seleziona prima la banca");
      return;
    }
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!isAccepted(droppedFile.name)) {
        toast.error("Seleziona un file PDF o Excel (.xlsx/.xls)");
        return;
      }
      setFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Seleziona un file PDF o Excel");
      return;
    }
    if (!selectedBankId) {
      toast.error("Seleziona la banca dell'estratto conto");
      return;
    }

    setUploading(true);
    
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('file', file);
      
      const params = new URLSearchParams({ locale_id: localeId });
      if (selectedBankId !== "__generic__") params.set("banca_id", selectedBankId);
      const url = `${API}/estratto-conto/parse?${params.toString()}`;
      
      const response = await axios.post(url, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast.success(`Estratte ${response.data.num_uscite} uscite`);
        onSuccess(response.data);
        handleClose();
      } else {
        toast.error("Errore nell'analisi del file");
      }
    } catch (error) {
      console.error("Errore upload:", error);
      const message = error.response?.data?.detail || "Errore nell'elaborazione del file";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploading(false);
    setDragOver(false);
    setSelectedBankId("");
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md bg-card border-border" data-testid="import-statement-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Importa Estratto Conto
          </DialogTitle>
          <DialogDescription className="sr-only">
            Seleziona la banca e carica un estratto conto per importarne esclusivamente le uscite.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Info box */}
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-3">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div className="text-sm text-foreground/80">
                <p>Carica il file dell&apos;estratto conto bancario (PDF o Excel).</p>
                <p className="mt-1 text-muted-foreground text-xs">
                  Verranno estratte automaticamente le <strong>uscite</strong> e confrontate con i dati esistenti.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-foreground flex items-center gap-2">
              <Landmark className="h-4 w-4" /> Banca dell&apos;estratto conto {banchePos.length > 1 ? "*" : ""}
            </Label>
            {banchePos.length <= 1 ? (
              <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2.5" data-testid="statement-bank-auto-selection">
                <span className="font-medium text-foreground">{banchePos[0]?.nome || "Generico"}</span>
                <span className="text-xs text-muted-foreground">Automatica</span>
              </div>
            ) : (
              <Select value={selectedBankId} onValueChange={(value) => { setSelectedBankId(value); setFile(null); }}>
                <SelectTrigger className="bg-card border-border text-foreground" data-testid="statement-bank-select">
                  <SelectValue placeholder="Seleziona la banca" />
                </SelectTrigger>
                <SelectContent>
                  {banchePos.map((bank) => (
                    <SelectItem key={bank.id} value={bank.id} data-testid={`statement-bank-option-${bank.id}`}>{bank.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Drop zone */}
          <div
            className={`
              border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer
              ${dragOver 
                ? 'border-primary bg-primary/5' 
                : file 
                  ? 'border-lime-500/50 bg-gray-100 dark:bg-gray-800/50' 
                  : 'border-border hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-800/30'
              }
            `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => selectedBankId ? fileInputRef.current?.click() : toast.error("Seleziona prima la banca")}
            data-testid="import-statement-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="import-statement-file-input"
            />
            
            {file ? (
              <div className="space-y-2">
                <FileText className="h-10 w-10 mx-auto text-lime-500" />
                <p className="text-foreground font-medium text-sm truncate max-w-[250px] mx-auto" title={file.name}>
                  {file.name}
                </p>
                <p className="text-muted-foreground text-xs">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="text-red-400 hover:text-red-300 text-xs"
                  data-testid="statement-file-remove-button"
                >
                  Rimuovi
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="text-foreground">
                  Trascina qui il file o <span className="text-primary">clicca per selezionare</span>
                </p>
                <p className="text-muted-foreground text-sm">
                  PDF oppure Excel (.xlsx / .xls)
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleClose}
            disabled={uploading}
            className="border-border text-foreground hover:bg-accent hover:text-accent-foreground"
            data-testid="statement-import-cancel-button"
          >
            Annulla
          </Button>
          <Button 
            onClick={handleUpload}
              disabled={!file || !selectedBankId || uploading}
            className="bg-primary hover:bg-primary/90 text-white"
            data-testid="import-statement-upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Elaborazione...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Analizza file
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
